import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { WorkspaceRevoked } from "@/components/app/workspace-revoked";
import { LoginCard } from "@/components/auth/login-card";
import { OperationsNoAccess } from "@/components/operations/operations-no-access";
import { OperationsNoWorkspaces } from "@/components/operations/operations-no-workspaces";
import { useHasOpsAccess } from "@/hooks/use-ops-access";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { claimSocket } from "@/lib/socket-owner";
import { opsAuthStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { opsUiStore } from "@/lib/stores/ops-ui-store";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/operations")({
	head: () => ({ meta: [{ title: "Operations - ELASO" }] }),
	component: RouteComponent,
});

function Centered({ label }: { label: string }) {
	return (
		<div className="flex h-full flex-col items-center justify-center gap-3">
			<Loader2 className="size-6 animate-spin text-muted-foreground" />
			<p className="text-muted-foreground">{label}</p>
		</div>
	);
}

function RouteComponent() {
	// The operations session, with its OWN Firebase app, independent of `/app`'s
	// (TECH-3151). Everything identity-shaped on this route reads from here, so
	// another user signed in elsewhere in the browser has no bearing on the portal.
	const user = useStore(opsAuthStore, (s) => s.user);
	const initialized = useStore(opsAuthStore, (s) => s.initialized);
	const login = useStore(opsAuthStore, (s) => s.login);
	const logout = useStore(opsAuthStore, (s) => s.logout);
	const { isConnected, connectionEpoch } = useWsConnected();

	const workspaces = useStore(coreStore, (s) => s.workspaces);
	const connectStatus = useStore(coreStore, (s) => s.connectStatus);
	const revokedReason = useStore(coreStore, (s) => s.revokedReason);
	const fetchWorkspaces = useStore(coreStore, (s) => s.fetchWorkspaces);
	const setCurrentWorkspace = useStore(coreStore, (s) => s.setCurrentWorkspace);
	const clearWorkspaceData = useStore(coreStore, (s) => s.clearWorkspaceData);
	// Base workspace data the ops views read (users, channels and categories,
	// presences, broadcast). /operations self-loads it and does not assume /app ran.
	const fetchChannels = useStore(coreStore, (s) => s.fetchChannels);
	const fetchUsers = useStore(coreStore, (s) => s.fetchUsers);
	const fetchPresences = useStore(coreStore, (s) => s.fetchPresences);
	const fetchBroadcastState = useStore(coreStore, (s) => s.fetchBroadcastState);
	const fetchDocumentsWithPermissions = useStore(
		coreStore,
		(s) => s.fetchDocumentsWithPermissions
	);
	const fetchAttendanceSessions = useStore(
		coreStore,
		(s) => s.fetchAttendanceSessions
	);
	const fetchSessions = useStore(coreStore, (s) => s.fetchSessions);
	const fetchAccessibleOpsTools = useStore(
		coreStore,
		(s) => s.fetchAccessibleOpsTools
	);
	// Which workspaces the portal can be USED in (TECH-3284). See below.
	const fetchOpsWorkspaceIds = useStore(
		coreStore,
		(s) => s.fetchOpsWorkspaceIds
	);
	const opsWorkspaceIds = useStore(coreStore, (s) => s.opsWorkspaceIds);

	// This session's own workspace selection, persisted in `ops-ui-store` rather
	// than the shared `ui-store`. With two accounts signed in at once a single
	// shared selection would let one surface re-point the other.
	const currentWorkspaceId = useStore(opsUiStore, (s) => s.currentWorkspaceId);
	const setCurrentWorkspaceId = useStore(
		opsUiStore,
		(s) => s.setCurrentWorkspaceId
	);

	// The portal's own workspace list: the `workspace.list` rows intersected with
	// the ids `operations.listWorkspaces` says the caller can open a tool in
	// (TECH-3284). An INTERSECTION rather than a replacement, because
	// `coreStore.workspaces` is the shared Participation map that `/app` reads,
	// and `workspace-announce` patches it a row at a time with `mode: "append"`
	// on a rename or a switch-on. Filtering the map itself would let one of those
	// frames put a workspace the caller only mentors in back into the portal's
	// picker. Order is the map's, which is `workspace.list`'s `createdAt` ascending.
	const opsWorkspaces = useMemo(() => {
		if (!opsWorkspaceIds) return [];
		const eligible = new Set(opsWorkspaceIds);
		return Array.from(workspaces.values()).filter((w) => eligible.has(w.id));
	}, [workspaces, opsWorkspaceIds]);

	const [rolesLoaded, setRolesLoaded] = useState(false);
	// Whether BOTH workspace lists have RESOLVED on this connection, the
	// Participation rows and the operations-capable ids. An empty map before then
	// is "still loading", not "no workspaces", and a failed fetch of either
	// never flips it, so a load error keeps the loading state rather than
	// misreporting an empty account (TECH-3214, extended to the ops list by
	// TECH-3284, because the two are read together and concluding from one while
	// the other is outstanding would report "no operations access" mid-load).
	const [workspacesLoaded, setWorkspacesLoaded] = useState(false);

	// Take the tab's single socket for the operations session. A no-op when this
	// surface already owns it; arriving from `/app` this tears that session's
	// connection down, clears the socket-scoped stores and reconnects as whoever
	// is signed in here.
	useEffect(() => {
		claimSocket("operations").catch((error) => {
			console.error("Failed to claim the socket for /operations:", error);
		});
	}, []);

	// Route-level ops access is decided by the SERVER-authoritative accessible-tool
	// set (docs/adr/27-the-operations-client.md), not the client-derived role
	// check. `fetchAccessibleOpsTools` commits its store slice synchronously
	// inside its own awaited resolution (it reads `result.tools` off the SUCCESS
	// frame), so once the gate's Promise.all resolves this value is committed.
	// The role-name check this replaced instead depended on two *separately*
	// dispatched UPDATE_STATE streams (`users.roles` matrix plus `roles` map)
	// landing in the store before the `rolesLoaded` flip, with
	// `clearWorkspaceData()` wiping both mid-gate, which raced and bounced
	// legitimate ops admins to /app. It is gone entirely as of TECH-3239, and
	// nothing on this surface names a role.
	//
	// Counts TABBED tools only since TECH-3284, sharing one definition with
	// `operations.listWorkspaces` so "listed" and "enterable" are the same
	// answer. The payload still carries the orphans (`/app` reads the `broadcast`
	// one), but a grant on an orphan alone opens an empty sidebar, which is not
	// access.
	const hasOpsAccess = useHasOpsAccess();

	// Signature of the caller's own role refs in the active workspace. Drives the
	// live re-validation effect below so a role grant or revoke that arrives while
	// the route is mounted re-pulls the authoritative tool set.
	const callerRoleSignature = useStore(coreStore, (s) => {
		const refs = user?.uid ? s.users.get(user.uid)?.roles : undefined;
		if (!refs) return "";
		return refs
			.map((r) => (r.kind === "named" ? r.id : r.kind))
			.sort()
			.join(",");
	});

	// The id of the workspace whose data we've already loaded on the CURRENT
	// connection. Guards the loader effect below against re-running when the
	// `workspaces` Map identity changes (the live `workspace.list` pushes
	// mode: "set", replacing the whole map), which would otherwise re-fire the
	// workspace preamble and gate (workspace.setCurrent, users.roles, roles.list,
	// getAccessibleTools) a second time. Reset on every socket open so a reconnect
	// reloads.
	const loadedWorkspaceRef = useRef<string | null>(null);

	// Drop the per-connection guards on every socket OPEN, not only on a visible
	// disconnect. Keyed on `connectionEpoch` because `isConnected` cannot carry
	// this. The hook holds it at true for 30s after a close, so signing out and
	// back in inside that window never flips it (TECH-3051). Without the epoch
	// the previous connection's "list resolved" flag stays standing over the map
	// that sign-out emptied, and the route reports an admin's account as having
	// no workspaces at all (TECH-3261). The guard ref drops with it, or the gate
	// below stays satisfied by the old workspace id and the shell comes back
	// with none of its channel or roster data behind it.
	useEffect(() => {
		loadedWorkspaceRef.current = null;
		setWorkspacesLoaded(false);
	}, [isConnected, connectionEpoch]);

	// Load the workspace list once per CONNECTION. `fetchWorkspaces` commits the
	// list to the store before resolving, so flipping the flag in the same
	// microtask can never observe a stale empty map for an account that has
	// workspaces (TECH-3214). Runs after the reset above, which is declared
	// first so a new socket never inherits the old one's resolved flag.
	useEffect(() => {
		if (!isConnected) return;
		let cancelled = false;
		Promise.all([fetchWorkspaces(), fetchOpsWorkspaceIds()])
			.then(([, opsListed]) => {
				if (cancelled) return;
				// `fetchOpsWorkspaceIds` RESOLVES on failure (it leaves its slice
				// alone rather than emptying it), so the failure has to be turned back
				// into one here, the same shape as the `!toolsLoaded` throw in the gate
				// below. Otherwise a transient `operations.listWorkspaces` error would
				// read as "capable in no workspace" and show an authorised admin the
				// no-access screen.
				if (!opsListed) {
					throw new Error("operations.listWorkspaces failed");
				}
				setWorkspacesLoaded(true);
			})
			.catch((error) => {
				console.error("Failed to load the workspace list:", error);
			});
		return () => {
			cancelled = true;
		};
	}, [isConnected, connectionEpoch, fetchWorkspaces, fetchOpsWorkspaceIds]);

	// What the empty state's "Check again" runs. It re-reads BOTH facts, because
	// the two arrive together. The reason an account has no workspace is usually
	// that it has just been added to its first one, and the tool grant that makes
	// that workspace usable lands in the same breath. Refreshing only the
	// Participation list would move the account off the no-workspaces screen and
	// onto the no-access one, which has no retry, on stale eligibility, and
	// leave it stranded there until the socket reconnects.
	//
	// Rejects on a failed ops read for the same reason the effect above throws.
	// `fetchOpsWorkspaceIds` resolves `false` rather than rejecting, and the
	// button's own handler needs a rejection to show "couldn't check" instead of
	// silently reporting nothing new.
	//
	// SEQUENTIAL, eligibility first, deliberately, not a `Promise.all`. Both
	// reads commit to the shared store as they land, and the branches below
	// re-render on each, so a concurrent pair whose LIST read succeeds and whose
	// ELIGIBILITY read fails would publish the new workspace against the stale
	// empty eligibility. This screen unmounts for the no-access one, which has
	// no retry, before the failure can be reported on it. Reading eligibility
	// first means a failure there leaves the map untouched and the user on the
	// screen that still has the button.
	const refreshWorkspaceList = useCallback(async () => {
		if (!(await fetchOpsWorkspaceIds())) {
			throw new Error("operations.listWorkspaces failed");
		}
		await fetchWorkspaces();
	}, [fetchWorkspaces, fetchOpsWorkspaceIds]);

	// Auto-select the first OPERATIONS-CAPABLE workspace if the persisted one is
	// gone, or was never one the portal can be used in (TECH-3284). Selecting
	// blindly from the Participation list is the reported bug. A user who mentors
	// program A and administers program B was dropped into A, told they had no
	// operations access, and left on the one screen with no workspace switcher on
	// it. Re-pointing is silent; the no-access screen below then means what it
	// says, which is "nowhere", not "not this one".
	//
	// Not while revoked (TECH-3253). A switched-off workspace leaves the map, and
	// this effect reads that as "the persisted one is gone", so it would move the
	// portal to a DIFFERENT program and the loader below would fetch that
	// program's roster, all behind the terminal screen. Holding the id still keeps
	// `workspaceExists` false, which is what stops the whole chain: one guard here
	// rather than one on each loader. Both of the terminal screen's actions reload
	// or sign out, and the reload re-picks from a clean store.
	useEffect(() => {
		if (connectStatus === "revoked") return;
		// Never re-point off a half-resolved list. Before both fetches land,
		// `opsWorkspaces` is empty for want of an answer, not for want of access.
		if (!workspacesLoaded) return;
		if (opsWorkspaces.length === 0) return;
		if (!opsWorkspaces.some((w) => w.id === currentWorkspaceId)) {
			setCurrentWorkspaceId(opsWorkspaces[0].id);
		}
	}, [
		opsWorkspaces,
		workspacesLoaded,
		currentWorkspaceId,
		setCurrentWorkspaceId,
		connectStatus,
	]);

	// Workspace preamble → AWAIT role load (the gate) → fire the rest in parallel.
	// Only load a workspace we've confirmed the portal can be used in. A stale
	// persisted id would make `workspace.setCurrent` reject with "Workspace not
	// found"; the auto-select effect corrects currentWorkspaceId, re-running this.
	// Since TECH-3284 the guard is the operations-capable list rather than the
	// Participation map, a strictly narrower set, so it still covers the
	// "not found" case, and it additionally stops the whole preamble from firing
	// against a workspace the caller only mentors in (which would fetch that
	// program's roster before the gate turned them away).
	const workspaceExists = currentWorkspaceId
		? opsWorkspaces.some((w) => w.id === currentWorkspaceId)
		: false;
	useEffect(() => {
		if (!(isConnected && currentWorkspaceId && workspaceExists)) return;
		// Idempotent per connection. The live `workspace.list` replaces the
		// `workspaces` Map (mode: "set"), so depending on a boolean instead of the
		// Map keeps the identity churn from re-firing the gate. The ref also blocks
		// a redundant reload when only currentWorkspaceId's surrounding state moves.
		if (loadedWorkspaceRef.current === currentWorkspaceId) return;
		loadedWorkspaceRef.current = currentWorkspaceId;
		let cancelled = false;
		setRolesLoaded(false);

		(async () => {
			await setCurrentWorkspace(currentWorkspaceId);
			if (cancelled) return;

			// Clear any prior workspace's data before reloading (handles switch).
			clearWorkspaceData();

			// Gate inputs: caller roles and roster. Awaited so "loading" is
			// distinguishable from "no access" before we decide what to render.
			const [, , toolsLoaded] = await Promise.all([
				ws.sendWithPromise({ id: ulid(), procedure: "users.roles", data: {} }),
				ws.sendWithPromise({ id: ulid(), procedure: "roles.list", data: {} }),
				// Server-driven tab visibility
				// (docs/adr/27-the-operations-client.md), loaded with the gate inputs
				// so the sidebar renders the correct tools on first paint.
				fetchAccessibleOpsTools(),
			]);
			if (cancelled) return;
			// A tool lookup that FAILED is not an empty grant set. It resolves
			// rather than rejecting (the slice is left alone on purpose), so the
			// failure has to be turned back into one here or the gate below would
			// read "we asked and got nothing" off a question that never got an
			// answer, and show an authorised admin the no-access screen after one
			// transient `operations.getAccessibleTools` error. Thrown so it takes
			// the same path as a failing `users.roles`. The guard clears, this
			// connection's `rolesLoaded` never flips, and the route holds the
			// loading state (the TECH-3214 rule, one step further along).
			if (!toolsLoaded) {
				throw new Error("operations.getAccessibleTools failed");
			}
			setRolesLoaded(true);

			// Full workspace dataset the ops views consume, parallel and
			// fire-and-forget (small admin audience). /operations self-loads
			// everything; it must not assume /app populated core-store. The ops
			// permission editors need every channel, including ones the admin has no
			// access to, so this uses `channel.listAll`, not the accessible-only
			// `channel.list`.
			fetchChannels(undefined, true);
			fetchUsers();
			fetchPresences();
			fetchBroadcastState();
			fetchDocumentsWithPermissions();
			fetchAttendanceSessions();
			fetchSessions();
		})().catch((error) => {
			// Clear the guard so the next render (or a corrected workspace id) can
			// retry. A failed gate must not leave the route stuck on a half-load.
			if (loadedWorkspaceRef.current === currentWorkspaceId) {
				loadedWorkspaceRef.current = null;
			}
			console.error("Failed to load operations workspace data:", error);
		});

		return () => {
			cancelled = true;
		};
	}, [
		isConnected,
		currentWorkspaceId,
		workspaceExists,
		setCurrentWorkspace,
		clearWorkspaceData,
		fetchChannels,
		fetchUsers,
		fetchPresences,
		fetchBroadcastState,
		fetchDocumentsWithPermissions,
		fetchAttendanceSessions,
		fetchSessions,
		fetchAccessibleOpsTools,
	]);

	// Live re-validation. Once the route is loaded, any change to the caller's
	// role refs (a grant or revoke broadcast) re-pulls the server-authoritative
	// tool set, so `hasOpsAccess`, and therefore the no-access screen below, stays
	// correct. `fetchAccessibleOpsTools` only overwrites the slice on success, so a
	// transient read failure cannot empty it and eject the admin.
	useEffect(() => {
		if (rolesLoaded) {
			fetchAccessibleOpsTools();
		}
	}, [rolesLoaded, callerRoleSignature, fetchAccessibleOpsTools]);

	if (!initialized) {
		return <Centered label="Loading..." />;
	}

	// The portal's own login, rendered IN PLACE. Deliberately not a redirect to
	// `/`, which would drop the deep link (and land the user in `/app`
	// afterwards), and `/` signs into a different session entirely.
	if (!user) {
		return (
			<div className="flex h-full items-center justify-center p-4">
				<div className="w-full max-w-md">
					<LoginCard
						description="Sign in to the operations portal"
						onSubmit={login}
						sentryContext="ops_auth_login"
						title="Operations"
					/>
				</div>
			</div>
		);
	}

	// Terminal: access to the workspace this portal is pointed at ended
	// mid-session, either a participation revoke or the workspace being switched
	// off (TECH-3253). This route had no `connectStatus` branch at all, so a staff
	// user watched every procedure start failing.
	//
	// Checked ahead of the empty-list branch below, mirroring the order in
	// `routes/app/route.tsx`. That branch does cover the case where this was the
	// account's only workspace, because the row retraction empties the map, but
	// with others left, `opsUiStore.currentWorkspaceId` still points at the one
	// that is gone, and nothing else here would say so.
	if (connectStatus === "revoked") {
		return (
			<WorkspaceRevoked
				hasOtherWorkspaces={workspaces.size > 0}
				onContinue={() => window.location.reload()}
				onLogout={logout}
				reason={revokedReason ?? "removed"}
			/>
		);
	}

	// The list resolved and this account participates in no workspace, so say so. The
	// auto-select effect has nothing to select, so `rolesLoaded` never flips and
	// the loading branch below would otherwise win forever (TECH-3214). The
	// check re-evaluates on any store update, so a workspace appearing, via
	// "Check again" or a live list push, resolves this without a reload.
	if (isConnected && workspacesLoaded && workspaces.size === 0) {
		return (
			<OperationsNoWorkspaces
				email={user.email}
				onRefresh={refreshWorkspaceList}
				onSignOut={logout}
			/>
		);
	}

	// In workspaces, but able to open a tool in none of them, a pure mentor or
	// learner who reached the portal (TECH-3284). Distinct from the branch above.
	// That account has no program at all and needs someone to add them to one;
	// this one is in programs, just not as staff.
	//
	// MUST precede the loading branch. With no operations-capable workspace there
	// is nothing for auto-select to pick, so the preamble never runs and
	// `rolesLoaded` never flips. Putting the loading branch first would hang the
	// route forever, which is exactly the shape of TECH-3214 one door along.
	if (isConnected && workspacesLoaded && opsWorkspaces.length === 0) {
		return <OperationsNoAccess email={user.email} onSignOut={logout} />;
	}

	if (!(isConnected && rolesLoaded)) {
		return <Centered label="Loading operations..." />;
	}

	// Signed in, but holds no tool grants here, so say so instead of bouncing.
	if (!hasOpsAccess) {
		return <OperationsNoAccess email={user.email} onSignOut={logout} />;
	}

	return <Outlet />;
}
